import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  class: "relative",
  "data-notif-root": ""
}
const _hoisted_2 = {
  key: 0,
  class: "absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
}
const _hoisted_3 = { class: "flex items-center justify-between p-3 border-b border-gray-700" }
const _hoisted_4 = /*#__PURE__*/_createElementVNode("p", { class: "text-white font-semibold" }, "Notifications", -1)
const _hoisted_5 = {
  key: 0,
  class: "flex items-center justify-between p-2 px-3 bg-gray-800/40 border-b border-gray-700 text-xs"
}
const _hoisted_6 = /*#__PURE__*/_createElementVNode("span", { class: "text-gray-300" }, "Activer les alertes navigateur ?", -1)
const _hoisted_7 = ["disabled"]
const _hoisted_8 = { class: "flex-1 overflow-y-auto" }
const _hoisted_9 = {
  key: 0,
  class: "p-6 text-center text-gray-500 text-sm"
}
const _hoisted_10 = {
  key: 1,
  class: "p-6 text-center text-gray-500 text-sm"
}
const _hoisted_11 = { key: 2 }
const _hoisted_12 = ["onClick"]
const _hoisted_13 = { class: "flex-1 min-w-0" }
const _hoisted_14 = {
  key: 0,
  class: "text-gray-400 text-xs mt-0.5 line-clamp-2"
}
const _hoisted_15 = { class: "text-gray-500 text-[11px] mt-1" }
const _hoisted_16 = {
  key: 0,
  class: "w-2 h-2 rounded-full bg-accent flex-shrink-0 mt-2"
}

import api from "@/api";
	import { onMounted, onBeforeUnmount, ref, computed } from "vue";
	import { useRouter } from "vue-router";
	import { getEcho } from "@/services/echo";
	import { userID } from "@/state/api/userState";
	import { isPushSupported, getPushPermissionState, enablePush } from "@/services/webPush";

	
export default /*#__PURE__*/_defineComponent({
  __name: 'NotificationBadge',
  setup(__props) {

	/**
	 * In-app notifications bell.
	 *
	 * Polls the backend every 60s for unread count + recent list. Clicking
	 * the bell opens a dropdown; clicking an item navigates to the linked
	 * route and marks it read.
	 */
	const router = useRouter();
	const open = ref(false);
	const items = ref<any[]>([]);
	const unread = ref(0);
	const loading = ref(false);
	const pushPermission = ref<NotificationPermission | "unsupported">("default");
	const pushBusy = ref(false);
	let pollHandle: any = null;
	let echoChannel: any = null;

	const canEnablePush = computed(() =>
		pushPermission.value !== "granted" && pushPermission.value !== "denied" && pushPermission.value !== "unsupported"
	);

	const onEnablePush = async () => {
		pushBusy.value = true;
		try {
			const r = await enablePush();
			if (r.ok) {
				pushPermission.value = "granted";
			} else {
				pushPermission.value = r.reason === "denied" ? "denied" : pushPermission.value;
			}
		} finally {
			pushBusy.value = false;
		}
	};

	const unreadDisplay = computed(() => (unread.value > 99 ? "99+" : String(unread.value)));

	const severityDot = (s: string) => {
		switch (s) {
			case "critical": return "bg-red-500";
			case "warning": return "bg-orange-400";
			case "success": return "bg-green-500";
			default: return "bg-accent";
		}
	};

	const relative = (iso: string) => {
		if (!iso) return "";
		const d = new Date(iso);
		const diff = Date.now() - d.getTime();
		const m = Math.floor(diff / 60000);
		if (m < 1) return "à l'instant";
		if (m < 60) return `il y a ${m} min`;
		const h = Math.floor(m / 60);
		if (h < 24) return `il y a ${h} h`;
		const j = Math.floor(h / 24);
		return `il y a ${j} j`;
	};

	const fetchAll = async () => {
		loading.value = true;
		try {
			const r: any = await api().notifications.getAll(false, 15);
			const payload = r.data?.data || r.data || {};
			items.value = payload.items || [];
			unread.value = payload.unread_count ?? 0;
		} catch (err) {
			console.warn("notifications fetch failed", err);
		}
		loading.value = false;
	};

	const fetchUnread = async () => {
		try {
			const r: any = await api().notifications.unreadCount();
			const payload = r.data?.data || r.data || {};
			unread.value = payload.count ?? 0;
		} catch {
			// silent: transient errors shouldn't spam the console every poll
		}
	};

	const toggle = async () => {
		open.value = !open.value;
		if (open.value) await fetchAll();
	};

	const onItemClick = async (n: any) => {
		if (!n.read) {
			try {
				await api().notifications.markRead(n.id);
				n.read = true;
				unread.value = Math.max(0, unread.value - 1);
			} catch (e) { console.warn(e); }
		}
		if (n.link) {
			open.value = false;
			router.push({ name: n.link }).catch((e: any) => console.warn(e));
		}
	};

	const onMarkAll = async () => {
		try {
			await api().notifications.markAllRead();
			items.value.forEach((i: any) => (i.read = true));
			unread.value = 0;
		} catch (e) { console.warn(e); }
	};

	const onOutside = (e: MouseEvent) => {
		const t = e.target as HTMLElement;
		if (!t.closest("[data-notif-root]")) open.value = false;
	};

	const subscribeRealtime = () => {
		const uid = userID.value;
		if (!uid) return;
		try {
			echoChannel = getEcho().private(`users.${uid}`);
			echoChannel.listen(".notification.created", (payload: any) => {
				items.value = [payload, ...items.value].slice(0, 50);
				if (!payload.read) unread.value += 1;
			});
		} catch (err) {
			console.warn("[notif] echo subscribe failed", err);
		}
	};

	onMounted(async () => {
		await fetchUnread();
		// Polling kept as fallback when WebSocket is unreachable.
		pollHandle = setInterval(fetchUnread, 60_000);
		subscribeRealtime();
		pushPermission.value = isPushSupported() ? await getPushPermissionState() : "unsupported";
		document.addEventListener("click", onOutside);
	});

	onBeforeUnmount(() => {
		if (pollHandle) clearInterval(pollHandle);
		if (echoChannel) {
			try { echoChannel.stopListening(".notification.created"); } catch (e) { console.warn(e); }
		}
		document.removeEventListener("click", onOutside);
	});

return (_ctx: any,_cache: any) => {
  const _component_Icon = _resolveComponent("Icon")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: "flex cursor-pointer",
      onClick: _withModifiers(toggle, ["stop"])
    }, [
      _createVNode(_component_Icon, {
        src: "/media/icons/notif-full.svg",
        css: "hover:opacity-70 w-6 h-6"
      }),
      (unread.value > 0)
        ? (_openBlock(), _createElementBlock("span", _hoisted_2, _toDisplayString(unreadDisplay.value), 1))
        : _createCommentVNode("", true)
    ]),
    (open.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "absolute right-0 mt-2 w-[380px] max-h-[480px] bg-[#19202A] border border-gray-700 rounded-lg shadow-2xl z-50 flex flex-col",
          onClick: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"]))
        }, [
          _createElementVNode("div", _hoisted_3, [
            _hoisted_4,
            (unread.value > 0)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  class: "text-xs text-accent hover:underline",
                  onClick: onMarkAll
                }, "Tout marquer comme lu"))
              : _createCommentVNode("", true)
          ]),
          (canEnablePush.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                _hoisted_6,
                _createElementVNode("button", {
                  class: "text-accent hover:underline disabled:opacity-50",
                  disabled: pushBusy.value,
                  onClick: onEnablePush
                }, _toDisplayString(pushBusy.value ? "..." : "Activer"), 9, _hoisted_7)
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_8, [
            (loading.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_9, "Chargement…"))
              : (items.value.length === 0)
                ? (_openBlock(), _createElementBlock("div", _hoisted_10, " Aucune notification "))
                : (_openBlock(), _createElementBlock("div", _hoisted_11, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(items.value, (n) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: n.id,
                        class: _normalizeClass(["flex items-start gap-3 p-3 border-b border-gray-800 hover:bg-gray-800/50 cursor-pointer", { 'bg-gray-900/40': !n.read }]),
                        onClick: ($event: any) => (onItemClick(n))
                      }, [
                        _createElementVNode("span", {
                          class: _normalizeClass(["mt-1.5 w-2 h-2 rounded-full flex-shrink-0", severityDot(n.severity)])
                        }, null, 2),
                        _createElementVNode("div", _hoisted_13, [
                          _createElementVNode("p", {
                            class: _normalizeClass(["text-white text-sm font-medium truncate", { 'font-semibold': !n.read }])
                          }, _toDisplayString(n.title), 3),
                          (n.body)
                            ? (_openBlock(), _createElementBlock("p", _hoisted_14, _toDisplayString(n.body), 1))
                            : _createCommentVNode("", true),
                          _createElementVNode("p", _hoisted_15, _toDisplayString(relative(n.created_at)), 1)
                        ]),
                        (!n.read)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_16))
                          : _createCommentVNode("", true)
                      ], 10, _hoisted_12))
                    }), 128))
                  ]))
          ]),
          _createElementVNode("div", { class: "p-2 border-t border-gray-700 text-center" }, [
            _createElementVNode("button", {
              class: "text-xs text-gray-400 hover:text-accent",
              onClick: fetchAll
            }, "Actualiser")
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})